import { useEffect, useMemo, useState } from 'react'
import { Button, GridRow } from 'semantic-ui-react'
import { Alert } from '@carsys/ui'
import Input from '@cbgms/components/form/Input'
import { useTranslation } from 'react-i18next'
import { change, formValueSelector } from 'redux-form'
import { IRootReducerState } from 'state/reducers'
import { useDispatch, useSelector } from 'react-redux'
import { getHaynesCustomerByClientID, getHaynesLicensesByCustomer } from 'app-support/licenses/state/actions'
import HaynesCustomerDataSummary from './HaynesProCustomerDataSummary'
import { IDistributor, IHaynesCustomer, IHaynesLicense, IShopByCompany } from '@cbgms/api'
import { isRequired } from '@cbgms/base/utils/validations'
import HaynesProNewCustomer from './HaynesProNewCustomer'
import HaynesProNewLicense from './HaynesProNewLicense'
import HaynesClientIDSuggestions from './HaynesClientIDSuggestions'
import { KeyType, isKeyType } from '@cbgms/base/utils/keyevent'

const clientIDPropName = 'HaynesProLicenseSettings.ClientID'
const usernamePropName = 'HaynesProLicenseSettings.Username'
const passwordPropName = 'HaynesProLicenseSettings.Password'

interface props {
  form: string
  distributor: IDistributor
  shop?: IShopByCompany
  companyCountry: string
  companyUUID: string
}

const HaynesProFields = ({ form, distributor, shop, companyCountry, companyUUID }: props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [hasSearched, setHasSearched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [modeEditCustomer, setModeEditCustomer] = useState(false)
  const [modeAddAdditionalLicense, setModeAddAdditionalLicense] = useState(false)
  const [customerResult, setCustomerResult] = useState<IHaynesCustomer | null>(null)
  const [licenseResult, setLicenseResult] = useState<IHaynesLicense[]>([])
  const [inactiveLicenseResult, setInactiveLicenseResult] = useState<IHaynesLicense[]>([])
  const clientID = useSelector((state: IRootReducerState) => formValueSelector(form)(state, clientIDPropName))
  const distributorID = distributor?.HaynesPro?.DistributorID
  const clientIDPostfix = distributor?.HaynesPro?.DistributorClientPostfix

  const modeHasCustomer = hasSearched && customerResult
  const modeNoCustomer = hasSearched && !customerResult
  const modeNoLicense = hasSearched && !licenseResult?.length

  const haynesFieldShouldBeValid = useMemo(
    () => () => {
      return modeHasCustomer ? undefined : t('haynespro.license_configuration_incomplete')
    },
    [modeHasCustomer]
  )

  const reset = () => {
    dispatch(change(form, clientIDPropName, ''))
    dispatch(change(form, usernamePropName, ''))
    dispatch(change(form, passwordPropName, ''))
    setHasSearched(false)
    setCustomerResult(null)
    setLicenseResult([])
    setInactiveLicenseResult([])
    setModeEditCustomer(false)
    setModeAddAdditionalLicense(false)
  }

  const onSearch = useMemo(
    () => async (overwriteClientID?: string) => {
      // execute api call with value
      const currentClientID = overwriteClientID || clientID
      if (currentClientID && distributorID) {
        try {
          if (!isLoading) {
            setIsLoading(true)
            setModeEditCustomer(false)
            const customerData = await dispatch(getHaynesCustomerByClientID(distributorID, currentClientID))
            if (customerData?.Data?.CustomerID) {
              const licenseData = await dispatch(getHaynesLicensesByCustomer(distributorID, customerData.Data.CustomerID, 'ActiveLicense'))
              setLicenseResult(licenseData?.Data || [])
              const inactiveLicenseData = await dispatch(
                getHaynesLicensesByCustomer(distributorID, customerData.Data.CustomerID, 'InactiveLicense')
              )
              setInactiveLicenseResult(inactiveLicenseData?.Data || [])
            }

            setCustomerResult(customerData.Data)
            // store the username and password, so they are saved as haynes fields
            if (customerData.Data) {
              dispatch(change(form, usernamePropName, customerData.Data.Username))
              dispatch(change(form, passwordPropName, customerData.Data.Password))
            }
            setIsLoading(false)
            setHasSearched(true)
          }
        } catch {
          reset()
          setIsLoading(false)
        }
      }
    },
    [clientID, distributorID, isLoading]
  )
  const onSearchHandler = useMemo(() => () => onSearch(), [onSearch])

  // trigger onSearch if the initial value is set
  useEffect(() => {
    if (clientID !== '') {
      onSearch()
    }
  }, [])

  const editExistingCustomer = () => {
    setModeEditCustomer(true)
  }
  const onEditExistingCustomerCompleted = () => {
    onSearch()
    setModeEditCustomer(false)
  }

  const onNewLicenseCompleted = () => {
    onSearch()
    setModeAddAdditionalLicense(false)
  }

  const addAdditionalLicense = () => {
    setModeAddAdditionalLicense(true)
  }

  const onSuggestionClick = async (value: string) => {
    await dispatch(change(form, clientIDPropName, value))
    onSearch(value) // make sure to pass the new value, as state might not have updated yet
  }

  return (
    <>
      {!distributorID && <Alert severity='info'>{t('haynespro.distributor_configuration_missing_distributor_id')}</Alert>}

      <GridRow>
        <div>
          <Input
            name={clientIDPropName}
            label={t('Field.HaynesProLicenseSettings.ClientID')}
            required
            validate={[isRequired, haynesFieldShouldBeValid]}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (isKeyType(e, KeyType.EnterKey)) {
                onSearch()
              }
            }}
          >
            <input disabled={hasSearched} onBlur={onSearchHandler} data-testid='input-haynespro-clientid' />
            {!hasSearched ? (
              <>
                <Button
                  primary
                  icon='search'
                  onClick={onSearchHandler}
                  disabled={!clientID}
                  loading={isLoading}
                  data-testid='search-by-clientid'
                />
                <HaynesClientIDSuggestions companyUUID={companyUUID} onClick={onSuggestionClick} selectedShop={shop} />
              </>
            ) : (
              <Button data-testid='btn-reset-customer-search' onClick={reset}>
                {t('haynespro.reset_search')}
              </Button>
            )}
          </Input>
        </div>
      </GridRow>
      <GridRow>
        <div>
          {modeHasCustomer && customerResult && (
            <>
              <Alert severity='info'>{t('haynespro.found_existing_customer')}</Alert>
              <HaynesCustomerDataSummary
                haynesCustomer={customerResult}
                licenses={licenseResult}
                inactiveLicenses={inactiveLicenseResult}
              />
              <Button onClick={editExistingCustomer}>{t('haynespro.change_existing_customer')}</Button>
              <Button onClick={addAdditionalLicense}>{t('haynespro.add_additional_license')}</Button>
            </>
          )}
          {(modeNoCustomer || modeEditCustomer) && (
            <HaynesProNewCustomer
              alreadyExists={modeEditCustomer}
              distributorID={distributorID}
              clientID={clientID}
              clientIDPostfix={clientIDPostfix || ''}
              customer={customerResult}
              shop={shop}
              companyCountry={companyCountry}
              form={form}
              onCustomerCreate={onEditExistingCustomerCompleted}
            />
          )}
          {(modeNoLicense || modeAddAdditionalLicense) && customerResult && (
            <HaynesProNewLicense
              form={form}
              distributorID={distributorID}
              customerID={customerResult.CustomerID}
              onLicenseCreate={onNewLicenseCompleted}
              productTypes={customerResult.ProductTypes || []}
            />
          )}
        </div>
      </GridRow>
    </>
  )
}

export default HaynesProFields
